import type { TimelineI } from "tweenly"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { t } from "i18next"
import { RGBColor } from "react-color"
import { ActionCreators } from "redux-undo"
import { Status } from "../../constants/status"
import { defaultCanvas } from "../../data/defaults/objects.types.defaults"
import { get, put } from "../../helpers/ajax.helpers"
import { Step } from "../../hooks/useWizard"
import { AppThunkT } from "../store"
import { clearSelectedAnimationsAction } from "./activeAnimation.slice"
import { clearAllSelectedObjectsAction } from "./activeObject.slice"
import { getArchivedGraphicFulfilled } from "./archivedGraphics.slice"
import { getGraphicAssetsAsync } from "./assets.slice"
import { getFileFulfilled } from "./file.slice"
import { getGraphicFulfilled } from "./graphic.slice"
import { updateMasterTimeline } from "./masterTimeline.slice"
import { clearObjectsAction, clearTmpObjectsAsync, setObjectsExpandedAction } from "./objects.slice"
import { getThumbnailFulfilled } from "./thumbnail.slice"
import { clearTimelineAction } from "./timeline.slice"

export enum InitState {
    InitAndPlay = "initAndPlay",
    InitAndPause = "initAndPause",
    HideAndPause = "hideAndPause",
}
export enum EndState {
    EndAndClear = "endAndClear",
    EndAndPause = "endAndPause",
}
export type Timeline = {
    initState: InitState
    endState: EndState
    //TO DO in future extends after move other values from global.d.ts
    // minduration: number
    // repeat: number
    // labels: TimelineLabelI[]
    // freezeTime: number | null
    // onInitCode: CodeTriggerT | null
    // onUpdateCode: CodeTriggerT | null
}
export type RecentColor = {
    color: RGBColor
    hex: string
}

type Settings = {
    canvasOverflowOn: boolean
    timelineRowsExpanded: boolean
    objectsExpanded: boolean
    selectedCompanyId: string
    canvasGridOn: boolean
    canvasSnapToGridOn: boolean
    canvasSnapToObjectsOn: boolean
    canvasZoom: number
    canvasResolution: number[] //[width, height]
    horizontalSplitterSize: number[]
    subVerticalSplitterSize: number[]
    subHorizontalSplitterSize: number[]
    horizontalLeftSideBarSplitterSize: number[]
    timeline: Timeline
    currentStep: string
    recentColor: RecentColor[]
    fontFamily: string[]
    lockAspectRatio: boolean
    companyId?: string
    galleryFilters: GalleryFilter
    storeFilters: StoreFilter
}

export type SettingsKey = keyof Settings

type Setting = {
    [key in SettingsKey]?: any
}

type GalleryFilter = {
    companyId: string
    attribute: string
    createdBy: string
    sortedBy: string
    type: string
}

type StoreFilter = {
    createdBy: string
    sortedBy: string
}

type StateT = {
    value: {
        timeline: TimelineI
        settings: Settings
        visibleSaveForm: boolean
        openDeleteAlert: boolean
        openDialogToDuplicate: boolean
        openDialogCreateNew: boolean
        versionHistoryDetail: boolean
        openAlertTestGraphicDialog: boolean
        signSaveAs: boolean
        prevFile: string
        openMenuWizard: boolean
        openCanvasSettings: boolean
        isTimelineSettingChanged: boolean
        isEditingTextMode: boolean
    }
    status?: string
}
const DEFAULT_GALLERY_FILTERS = {
    companyId: "",
    attribute: "pageHeader.filterDefaultValue",
    createdBy: "pageHeader.filterDefaultValue",
    sortedBy: "pageHeader.sortedByNewest",
    type: "all",
}
const DEFAULT_STORE_FILTER = {
    createdBy: "pageHeader.filterDefaultValue",
    sortedBy: "pageHeader.sortedByNewest",
}

const initSettings: Settings = {
    canvasOverflowOn: false,
    timelineRowsExpanded: true,
    objectsExpanded: true,
    selectedCompanyId: "",
    canvasGridOn: false,
    canvasSnapToGridOn: false,
    canvasSnapToObjectsOn: true,
    canvasZoom: 100,
    canvasResolution: [defaultCanvas.width, defaultCanvas.height],
    horizontalSplitterSize: [75, 25],
    subVerticalSplitterSize: [66, 34],
    subHorizontalSplitterSize: [25, 75],
    horizontalLeftSideBarSplitterSize: [85, 15],
    timeline: {
        initState: InitState.InitAndPause,
        endState: EndState.EndAndPause,
    },
    currentStep: "",
    recentColor: [],
    lockAspectRatio: false,
    fontFamily: [],
    galleryFilters: DEFAULT_GALLERY_FILTERS,
    storeFilters: DEFAULT_STORE_FILTER,
}

const initialState: StateT = {
    value: {
        timeline: {} as TimelineI,
        settings: initSettings,
        visibleSaveForm: false,
        openDeleteAlert: false,
        openDialogToDuplicate: false,
        openDialogCreateNew: false,
        versionHistoryDetail: false,
        openAlertTestGraphicDialog: false,
        signSaveAs: false,
        prevFile: "",
        openMenuWizard: false,
        openCanvasSettings: false,
        isTimelineSettingChanged: false,
        isEditingTextMode: false,
    },
}

export const editorSlice = createSlice({
    name: "editor",
    initialState,
    reducers: {
        prevSaveFile: (state: StateT, action: PayloadAction<string>) => {
            state.value = {
                ...state.value,
                prevFile: action.payload,
            }
        },
        setOpenSaveForm: (state: StateT, action: PayloadAction<boolean>) => {
            state.value = {
                ...state.value,
                visibleSaveForm: action.payload,
            }
        },
        setOpenDeleteAlert: (state: StateT, action: PayloadAction<boolean>) => {
            state.value = {
                ...state.value,
                openDeleteAlert: action.payload,
            }
        },
        setOpenDialogToDuplicate: (state: StateT, action: PayloadAction<boolean>) => {
            state.value = {
                ...state.value,
                openDialogToDuplicate: action.payload,
            }
        },
        setOpenDialogCreateNew: (state: StateT, action: PayloadAction<boolean>) => {
            state.value = {
                ...state.value,
                openDialogCreateNew: action.payload,
            }
        },
        setOpenCanvasSettings: (state: StateT, action: PayloadAction<boolean>) => {
            state.value = {
                ...state.value,
                openCanvasSettings: action.payload,
            }
        },
        setIsTimelineSettingChanged: (state: StateT, action: PayloadAction<boolean>) => {
            state.value = {
                ...state.value,
                isTimelineSettingChanged: action.payload,
            }
        },
        setIsEditingTextMode: (state: StateT, action: PayloadAction<boolean>) => {
            state.value = {
                ...state.value,
                isEditingTextMode: action.payload,
            }
        },
        setLockAspectRationSettings: (state: StateT, action: PayloadAction<boolean>) => {
            state.value = {
                ...state.value,
                settings: {
                    ...state.value.settings,
                    lockAspectRatio: action.payload,
                },
            }
        },
        setOpenMenuWizard: (state: StateT, action: PayloadAction<boolean>) => {
            state.value = {
                ...state.value,
                openMenuWizard: action.payload,
            }
        },
        setOpenVersionHistoryDetail: (state: StateT, action: PayloadAction<boolean>) => {
            state.value = {
                ...state.value,
                versionHistoryDetail: action.payload,
            }
        },
        setOpenAlertTestGraphicDialog: (state: StateT, action: PayloadAction<boolean>) => {
            state.value = {
                ...state.value,
                openAlertTestGraphicDialog: action.payload,
            }
        },
        setSignSaveAs: (state: StateT, action: PayloadAction<boolean>) => {
            state.value = {
                ...state.value,
                signSaveAs: action.payload,
            }
        },
        getPreferencesPending: (state: StateT) => {
            state.status = undefined
        },
        getPreferencesFulfilled: (state: StateT, action: PayloadAction<Setting>) => {
            const data = action.payload
            state.status = Status.OK
            state.value = {
                ...state.value,
                settings: {
                    ...state.value.settings,
                    ...data,
                },
            }
        },
        getPreferencesRejected: (state: StateT, action: PayloadAction<string>) => {
            state.status = action.payload
        },
        setPreferencesPending: (state: StateT) => {
            state.status = undefined
        },
        setPreferencesFulfilled: (state: StateT, action: PayloadAction<Setting>) => {
            //TO DO need to refactor -> set nested object
            state.status = Status.OK
            state.value = {
                ...state.value,
                settings: {
                    ...state.value.settings,
                    ...action.payload,
                },
            }
        },

        setPreferencesRejected: (state: StateT, action: PayloadAction<string>) => {
            state.status = action.payload
        },
    },
})

export const {
    prevSaveFile,
    setOpenSaveForm,
    setOpenCanvasSettings,
    setIsTimelineSettingChanged,
    setIsEditingTextMode,
    setLockAspectRationSettings,
    setOpenVersionHistoryDetail,
    setOpenAlertTestGraphicDialog,
    setOpenMenuWizard,
    setSignSaveAs,
    setOpenDeleteAlert,
    setOpenDialogToDuplicate,
    setOpenDialogCreateNew,
    getPreferencesPending,
    getPreferencesFulfilled,
    getPreferencesRejected,
    setPreferencesPending,
    setPreferencesFulfilled,
    setPreferencesRejected,
} = editorSlice.actions
export default editorSlice.reducer

export const getPreferencesAsync = (): AppThunkT => async (dispatch) => {
    try {
        dispatch(getPreferencesPending())
        const preferences = await get<Settings>(`preferences`)
        const steps: Step[] = t("wizard:steps", { returnObjects: true })
        let modifiedPreferences = preferences

        const currentPathName = steps.find((x) => x.key === preferences.currentStep)?.pathname
        const actualPageSteps: Step[] = steps.filter((x) => x.pathname === currentPathName)

        //default first step when user not absolved tutorial
        if (!preferences?.currentStep) {
            modifiedPreferences = {
                ...preferences,
                currentStep: steps[0].key,
            }
        }
        //set first step in page when user refresh page or redirected to another page and back to page where ended tutorial
        if (currentPathName && actualPageSteps[0].key) {
            modifiedPreferences = {
                ...preferences,
                currentStep: actualPageSteps[0].key,
            }
        }

        dispatch(getPreferencesFulfilled(modifiedPreferences))
    } catch (error: any) {
        dispatch(getPreferencesRejected(error.message))
    }
}

export const setRecentColorPreferencesAsync =
    (value: any): AppThunkT =>
    async (dispatch, getState) => {
        try {
            let modifiedRecentColor: RecentColor[] = []
            const recentColor = getState().editor.value.settings.recentColor
            if (recentColor.find((x) => x.hex === value.hex)) {
                return
            } else {
                if (recentColor.length === 10) {
                    const filteredRecentColor = recentColor.filter((x, index) => index !== 9)
                    modifiedRecentColor = [
                        { color: value.rgb, hex: value.hex },
                        ...filteredRecentColor,
                    ]
                } else {
                    modifiedRecentColor = [{ color: value.rgb, hex: value.hex }, ...recentColor]
                }
                dispatch(setPreferencesFulfilled({ recentColor: modifiedRecentColor }))
                await put(`preferences/recentColor`, { data: modifiedRecentColor })
            }
        } catch (error: any) {
            dispatch(setPreferencesRejected(error.message))
        }
    }

export const setFontFamilyPreferencesAsync =
    (value: any): AppThunkT =>
    async (dispatch, getState) => {
        try {
            const fontFamily = getState().editor.value.settings.fontFamily
            let modifiedFondFamily: string[] = []
            if (fontFamily.find((x) => x === value)) {
                const filteredArray = fontFamily.filter((x) => x !== value)
                modifiedFondFamily = [value, ...filteredArray]
            } else if (fontFamily?.length === 5) {
                const filteredArray = fontFamily.filter((x, index) => index !== 4)
                modifiedFondFamily = [value, ...filteredArray]
            } else {
                modifiedFondFamily = [value, ...fontFamily]
            }
            dispatch(setPreferencesFulfilled({ fontFamily: modifiedFondFamily }))
            await put(`preferences/fontFamily`, { data: modifiedFondFamily })
        } catch (error: any) {
            dispatch(setPreferencesRejected(error.message))
        }
    }

// Merge fonts discovered during an import (HTML/ZIP/Pencil) into the user's font
// preferences so they show up in the font selector, and persist them to the backend.
export const addImportedFontsToPreferencesAsync =
    (fontNames: string[]): AppThunkT =>
    async (dispatch, getState) => {
        try {
            const currentFontPrefs = getState().editor.value.settings.fontFamily || []
            const newFonts = fontNames.filter((font) => !currentFontPrefs.includes(font))
            if (newFonts.length === 0) {
                return
            }
            const updatedFonts = [...newFonts, ...currentFontPrefs].slice(0, 10)
            dispatch(setPreferencesFulfilled({ fontFamily: updatedFonts }))
            await put(`preferences/fontFamily`, { data: updatedFonts })
        } catch (error: any) {
            dispatch(setPreferencesRejected(error.message))
        }
    }
export const setLockAspectRationAsync =
    (lockAspectRatio: boolean): AppThunkT =>
    async (dispatch) => {
        try {
            dispatch(setPreferencesFulfilled({ lockAspectRatio: !lockAspectRatio }))
            await put(`preferences/lockAspectRatio`, { data: !lockAspectRatio })
        } catch (error: any) {
            dispatch(setPreferencesRejected(error.message))
        }
    }

export const setPreferencesAsync =
    (key: SettingsKey, value: any): AppThunkT =>
    async (dispatch) => {
        try {
            dispatch(setPreferencesFulfilled({ [key]: value }))
            await put(`preferences/${key}`, { data: value })
        } catch (error: any) {
            dispatch(setPreferencesRejected(error.message))
        }
    }

export const setObjectsExpandedAsync =
    (value: boolean): AppThunkT =>
    async (dispatch) => {
        try {
            dispatch(setPreferencesFulfilled({ objectsExpanded: value }))
            dispatch(setObjectsExpandedAction(value))
            await put(`preferences/objectsExpanded`, { data: value })
        } catch (error: any) {
            dispatch(setPreferencesRejected(error.message))
        }
    }

export const resetGalleryFiltersPreferencesAsync = (): AppThunkT => async (dispatch) => {
    try {
        dispatch(setPreferencesFulfilled({ galleryFilters: DEFAULT_GALLERY_FILTERS }))
        await put(`preferences/galleryFilters`, { data: DEFAULT_GALLERY_FILTERS })
    } catch (error: any) {
        dispatch(setPreferencesRejected(error.message))
    }
}
export const resetStoreFiltersPreferencesAsync = (): AppThunkT => async (dispatch) => {
    try {
        dispatch(setPreferencesFulfilled({ storeFilters: DEFAULT_STORE_FILTER }))
        await put(`preferences/storeFilters`, { data: DEFAULT_STORE_FILTER })
    } catch (error: any) {
        dispatch(setPreferencesRejected(error.message))
    }
}

export const baseClearScene =
    (withHistoryClear: boolean): AppThunkT =>
    async (dispatch) => {
        dispatch(clearObjectsAction())
        dispatch(clearSelectedAnimationsAction())
        dispatch(clearTimelineAction())
        dispatch(updateMasterTimeline(0))

        if (withHistoryClear) {
            dispatch(ActionCreators.clearHistory())
        }
    }

export const clear = (): AppThunkT => async (dispatch) => {
    dispatch(baseClearScene(true))
}

export const clearAndKeepHistory = (): AppThunkT => async (dispatch) => {
    dispatch(baseClearScene(false))
}

export const clearAndReset =
    (graphicId?: string): AppThunkT =>
    async (dispatch) => {
        if (graphicId) await dispatch(clearTmpObjectsAsync(graphicId))
        await dispatch(getGraphicFulfilled())
        await dispatch(getGraphicAssetsAsync([], ""))
        await dispatch(getFileFulfilled())
        await dispatch(getArchivedGraphicFulfilled())
        await dispatch(getThumbnailFulfilled({ id: undefined }))
        await dispatch(clear())
    }

export const undo = (): AppThunkT => async (dispatch, getState) => {
    const state = getState()
    const timeToSeek = state.masterTimeline.time

    const prevState = state.undoable.past[state.undoable.past.length - 1]
    if (!prevState.objects.value[state.activeObject.selected[0]!]) {
        await dispatch(clearAllSelectedObjectsAction())
    }

    dispatch(ActionCreators.undo())
    dispatch(updateMasterTimeline(timeToSeek))
}

export const redo = (): AppThunkT => async (dispatch, getState) => {
    const state = getState()
    const timeToSeek = state.masterTimeline.time
    dispatch(ActionCreators.redo())
    dispatch(updateMasterTimeline(timeToSeek))
}
